/* global DfApi, Turbo */

import { Controller } from '@hotwired/stimulus';
import {
  DraggingImageCreator,
  debounce,
  useEventListener,
  useIntersectionObserver,
} from '@/js/util';

/**
 * Generic draggable controller to enable drag-and-drop functionality for items within a Folder in a project.
 *
 * @example Complete usage with all parameters
 *   div[data-controller="draggable"
 *       data-draggable-item-selector-value="[data-draggable-item]"
 *       data-draggable-project-selector-value="[data-draggable-root]"
 *       data-draggable-item-list-selector-value="#project_designs_frame"
 *       data-draggable-item-dragging-class-value="custom-dragging"
 *       data-draggable-item-hover-class-value="custom-hover"]
 *
 *     // Draggable items (boards, fp_views, folders)
 *     div[draggable="true" data-draggable-item data-design-element-type="Board" data-design-element-id="123"]
 *     div[draggable="true" data-draggable-item data-design-element-type="FpView" data-design-element-id="789"]
 *     div[draggable="true" data-draggable-item data-design-element-type="Folder" data-design-element-id="456"]
 *
 * Required parameters:
 * - `data-draggable-item-selector-value`: CSS selector for draggable items
 * - `data-draggable-project-selector-value`: CSS selector for the project root element
 * - `data-draggable-item-list-selector-value`: CSS selector for the container holding all items
 *
 * Optional parameters and their defaults:
 * - `data-draggable-item-dragging-class-value`: CSS class applied during drag (default: 'draggable-dragging')
 * - `data-draggable-item-hover-class-value`: CSS class applied to drop targets on hover (default: 'draggable-hover')
 *
 * Required attributes for draggable items:
 * - `draggable="true"`: To make the element draggable
 * - `data-design-element-type`: Type of element ("Board", "FpView" or "Folder")
 * - `data-design-element-id`: Unique ID of the element
 */
export default class extends Controller {
  #draggingImages = new DraggingImageCreator(0.85);
  #currentX = 0;
  #previousX = 0;
  #createPlaceholderTimeout = null;

  static values = {
    itemSelector: String,
    projectSelector: String,
    itemListSelector: String,
    itemDraggingClass: {
      type: String,
      default: 'draggable-dragging',
    },
    itemHoverClass: {
      type: String,
      default: 'draggable-hover',
    },
  };

  connect() {
    useEventListener(this);
    useIntersectionObserver(this);

    this.draggingElement = null;
    this.selectedElements = [];
    this.dragStartIndex = null;
    this.projectId = $('meta[name=project-id]').attr('content');
    this.multiSelectCheckboxSelector = 'input.batch-element-select-checkbox';

    this.debouncedUpdateSorting = debounce(this.updateSorting.bind(this), 800);

    this.dragStart = this.dragStart.bind(this);
    this.dragEnd = this.dragEnd.bind(this);
    this.dragOver = this.dragOver.bind(this);
    this.dragEnter = this.dragEnter.bind(this);
    this.dragLeave = this.dragLeave.bind(this);
    this.dragDrop = this.dragDrop.bind(this);
    this.turboStreamRenderCallback = this.turboStreamRenderCallback.bind(this);
    this.updateCoordinatesCallback = this.updateCoordinates.bind(this);

    this.setup();
  }

  setup() {
    const observerOptions = {
      trackVisibility: true,
      delay: 200,
    };
    this.itemsObserver = this.createIntersectionObserver(
      this.createDraggingImageForItem.bind(this),
      observerOptions
    );

    for (const item of this.items) {
      this.itemsObserver.observe(item);
    }

    this.addElementEventListeners();

    this.addEventListener(document, 'turbo:before-stream-render', this.turboStreamRenderCallback);
    this.addEventListener(document, 'dragover', this.updateCoordinatesCallback);
    this.addEventListener(document.querySelector('main'), 'dragover', this.autoScrollWhenDragging);
  }

  disconnect() {
    this.#draggingImages.destroyDraggingImages();
  }

  addElementEventListeners() {
    this.addEventListener(this.element, 'dragstart', this.dragStart);
    this.addEventListener(this.element, 'dragend', this.dragEnd);

    this.addEventListener(this.element, 'dragover', this.dragOver);
    this.addEventListener(this.element, 'dragenter', this.dragEnter);
    this.addEventListener(this.element, 'dragleave', this.dragLeave);

    this.addEventListener(this.element, 'drop', this.dragDrop);
  }

  createDraggingImageForItem(entries) {
    for (const { isIntersecting, target } of entries) {
      if (isIntersecting) {
        this.#draggingImages.createDraggingImageForElement(target);
        this.itemsObserver.unobserve(target);
      }
    }
  }

  get items() {
    return this.element.querySelectorAll(this.itemSelectorValue);
  }

  projectFolder() {
    return this.element.querySelector(this.projectSelectorValue);
  }

  itemListContainer() {
    return document.querySelector(this.itemListSelectorValue);
  }

  isFolderElement(element) {
    return element && element.dataset.designElementType === 'Folder';
  }

  dragStart(e) {
    const targetElement = this.#closestItem(e.target);
    // A native drag of something outside an item, such as a link or selected text, stays native.
    if (!targetElement) {
      return;
    }

    let elementsStack = [targetElement];
    this.draggingElement = targetElement;
    e.dataTransfer.effectAllowed = 'move';
    this.element.classList.add('draggable-active');

    this.dragStartIndex = Array.from(this.items).indexOf(targetElement);

    if (targetElement.querySelector(this.multiSelectCheckboxSelector)?.checked) {
      const selectedElements = this.fetchSelectedElements();
      selectedElements.forEach((el) => el.classList.add(this.itemDraggingClassValue));
      const filteredElements = selectedElements.filter((el) => el !== targetElement);

      if (selectedElements.includes(targetElement)) {
        filteredElements.unshift(targetElement);
      }
      const reorderedElements = filteredElements.length > 0 ? filteredElements : selectedElements;
      elementsStack = reorderedElements;
    } else {
      targetElement.classList.add(this.itemDraggingClassValue);
    }
    this.#draggingImages.setStackedDraggingImages(e, elementsStack);
  }

  dragEnd() {
    if (!this.draggingElement) {
      return;
    }

    this.clearPlaceholderTimeout();
    this.element.classList.remove('draggable-active');
    this.removePlaceholders();
    const draggedElements =
      this.selectedElements.length > 1 ? this.selectedElements : [this.draggingElement];

    draggedElements.forEach((el) => {
      el.classList.remove(this.itemDraggingClassValue);
      this.#draggingImages.hideDraggingImage(el);
    });

    this.draggingElement = null;
    this.selectedElements = [];
    this.dragStartIndex = null;
  }

  dragOver(e) {
    e.preventDefault();
  }

  dragEnter(e) {
    const targetElement = this.#closestItem(e.target);
    if (!this.draggingElement || !targetElement || targetElement === this.draggingElement) {
      return;
    }

    if (this.projectFolder() === targetElement) {
      targetElement.classList.add(this.itemHoverClassValue);
      return this.removePlaceholders();
    }

    const isDraggingFolderOverFolder = this.#isDraggingFolderOverFolder(targetElement);
    let timeout = 0;
    if (!isDraggingFolderOverFolder) {
      // Add a delay so the folder doesn’t move immediately when dragging boards over it
      timeout = this.isFolderElement(targetElement) ? 750 : 0;
      targetElement.classList.add(this.itemHoverClassValue);
    }
    this.clearPlaceholderTimeout();
    this.#createPlaceholderTimeout = setTimeout(
      () => this.prependPlaceholder(targetElement),
      timeout
    );

    document.body.classList.toggle('drop-disabled', isDraggingFolderOverFolder);
    targetElement.classList.toggle(this.itemHoverClassValue, !isDraggingFolderOverFolder);
  }

  dragLeave(e) {
    const targetElement = this.#closestItem(e.target);
    const elementUnderCursor = document.elementFromPoint(e.clientX, e.clientY);
    const hoverSelection =
      targetElement === elementUnderCursor || targetElement?.contains(elementUnderCursor);

    if (!targetElement || hoverSelection) {
      return;
    }

    this.clearPlaceholderTimeout();
    targetElement.classList.remove(this.itemHoverClassValue);
    document.body.classList.remove('drop-disabled');
  }

  dragDrop(e) {
    if (!this.draggingElement) {
      return;
    }

    this.clearPlaceholderTimeout();
    const placeholderDrop = e.target.classList.contains('drag-placeholder');
    const dropTargetElementIdx = Array.from(this.itemListContainer().children).indexOf(e.target);
    const placeholderDropDiff = this.#movingLeft ? 1 : 0;
    const droppedInSameSpot =
      placeholderDrop && this.dragStartIndex === dropTargetElementIdx - placeholderDropDiff;
    let targetElement;

    document.body.classList.remove('drop-disabled');
    this.removePlaceholders();

    if (droppedInSameSpot) {
      return;
    } else if (placeholderDrop) {
      const dropTargetId = e.target.dataset.dropTargetId;
      targetElement = this.element.querySelector(`#${dropTargetId}`);
    } else {
      targetElement = this.#closestItem(e.target);
    }

    if (!targetElement) {
      return;
    }

    targetElement.classList.remove(this.itemHoverClassValue);

    const droppedInFolder =
      targetElement === this.projectFolder() || this.isFolderElement(targetElement);
    const noSelectedFolders = !this.anySelectedFolder();

    if (droppedInFolder && noSelectedFolders && !placeholderDrop) {
      this.handleFolderDrop(targetElement);
    } else {
      this.handleItemDrop(targetElement);
    }
  }

  async handleFolderDrop(targetFolderElement) {
    if (this.anySelectedFolder()) {
      return;
    }

    const draggedElements =
      this.selectedElements.length > 1 ? this.selectedElements : [this.draggingElement];

    const designElements = draggedElements.map((el) => {
      const { designElementType, designElementId } = el.dataset;
      return { designElementType, designElementId };
    });
    const folderId = this.isFolderElement(targetFolderElement)
      ? targetFolderElement.dataset.designElementId
      : null;

    const response = await this.#api.moveDesignElementsToFolder(
      this.projectId,
      designElements,
      folderId,
      'turbostream'
    );
    const responseText = await response.text();
    Turbo.renderStreamMessage(responseText);
  }

  handleItemDrop(dropTargetElement) {
    const parent = this.itemListContainer();
    const children = Array.from(parent.children);
    const dropTargetElementIdx = children.indexOf(dropTargetElement);
    const draggingElementIdx = children.indexOf(this.draggingElement);

    if (dropTargetElementIdx > draggingElementIdx) {
      dropTargetElement.after(this.draggingElement);
    } else {
      parent.insertBefore(this.draggingElement, dropTargetElement);
    }

    let insertTarget = this.draggingElement;
    this.selectedElements.forEach((element) => {
      if (element === this.draggingElement) {
        return;
      }
      insertTarget.after(element);
      insertTarget = element;
    });

    this.debouncedUpdateSorting();
  }

  sortedItemIds() {
    const newOrder = [];
    this.items.forEach((item) => {
      const { designElementType, designElementId } = item.dataset;

      if (!designElementType || !designElementId) {
        return;
      }

      newOrder.push({
        design_element_type: designElementType,
        design_element_id: designElementId,
      });
    });

    return newOrder;
  }

  updateSorting() {
    const sortedIds = this.sortedItemIds();
    this.#api.sortDesignElements(this.projectId, sortedIds);
  }

  fetchSelectedElements() {
    this.selectedElements = [];
    document.querySelectorAll(this.itemSelectorValue).forEach((item) => {
      const checkbox = item.querySelector(this.multiSelectCheckboxSelector);
      if (checkbox && checkbox.checked) {
        this.selectedElements.push(item);
      }
    });

    return this.selectedElements;
  }

  anySelectedFolder() {
    return (
      this.selectedElements.some((el) => this.isFolderElement(el)) ||
      this.isFolderElement(this.draggingElement)
    );
  }

  turboStreamRenderCallback(event) {
    const originalRender = event.detail.render;

    event.detail.render = (streamElement) => {
      const template = streamElement.querySelector('template');
      const content = template?.content;
      const draggableItem = content?.querySelector(this.itemSelectorValue);

      if (draggableItem === null) {
        return originalRender(streamElement);
      }

      originalRender(streamElement).then(() => {
        const item = Array.from(this.items).find((el) => el.id === draggableItem.id);
        if (item) {
          this.#draggingImages.createDraggingImageForElement(item);
        }
      });
    };
  }

  prependPlaceholder(targetElement) {
    const parent = this.itemListContainer();

    this.removePlaceholders();

    this.element.classList.add('draggable-with-placeholder');
    const placeholder = document.createElement('div');
    placeholder.classList.add('drag-placeholder');
    placeholder.classList.add(`drag-placeholder-${targetElement.id}`);
    placeholder.dataset.dropTargetId = targetElement.id;

    if (this.#movingLeft) {
      parent.insertBefore(placeholder, targetElement);
    } else {
      targetElement.after(placeholder);
    }
  }

  removePlaceholders() {
    this.element.classList.remove('draggable-with-placeholder');
    this.itemListContainer()
      .querySelectorAll('.drag-placeholder')
      .forEach((el) => el.remove());
  }

  clearPlaceholderTimeout() {
    clearTimeout(this.#createPlaceholderTimeout);
    this.#createPlaceholderTimeout = null;
  }

  updateCoordinates(event) {
    if (event.clientX !== this.#currentX) {
      this.#previousX = this.#currentX;
      this.#currentX = event.clientX;
    }
  }

  autoScrollWhenDragging({ clientY, currentTarget: container }) {
    const rect = container.getBoundingClientRect();
    const offset = 40;
    let speed = 0;

    if (clientY < rect.top + offset) {
      speed = -5;
    }
    if (clientY > rect.bottom - offset) {
      speed = 5;
    }

    speed !== 0 && requestAnimationFrame(() => (container.scrollTop += speed));
  }

  #isDraggingFolderOverFolder(target) {
    const hoverFolder = this.isFolderElement(target);
    const draggingFolder = this.draggingElement.dataset.designElementType === 'Folder';

    return hoverFolder && draggingFolder;
  }

  get #movingLeft() {
    return this.#currentX < this.#previousX;
  }

  // Dragging selected text targets a Text node, which has no closest().
  #closestItem(target) {
    const element = target instanceof Element ? target : target?.parentElement;
    return element?.closest(this.itemSelectorValue) ?? null;
  }

  // DfApi comes from the legacy bundle, which may not have run yet when Stimulus builds this
  // controller, so it is looked up on use.
  get #api() {
    return new DfApi();
  }
}
